// Amenazas naturales: matriz amenaza × vulnerabilidad 4×4 (PE-15).
// Kevin, 26/10/2026 (PE-19 y PE-20):
//  - Sismo: automático desde la zona sísmica de la NEC-SE-DS (§10.2, Tabla 19, factor Z por población):
//    zona I → amenaza media; zonas II a V → alta; zona VI → muy alta (la NEC caracteriza la zona I como
//    intermedia, II a V como alta y VI como muy alta). La Tabla 19 todavía no está transcrita: la zona se
//    elige a mano.
//  - Volcanes, inundaciones y otras: el responsable elige el nivel y la fuente es obligatoria (mapa del
//    IGEPN o PDOT del GAD).
//  - Vulnerabilidad: RESUELTA por Claude el 27/10/2026 (punto 17 / PE-19; Kevin solo revisa): por amenaza,
//    con criterios de preparación en personas, recursos y sistemas (pe-vulnerabilidad.ts, modo 'criterios').
//    Se conservan los modos 'cumplimiento' (% de la verificación normativa) y 'manual'.

import { etiquetaNivel, FUENTE_MATRIZ_AMENAZAS, MATRIZ_AMENAZA_VULNERABILIDAD, Nivel4 } from './pe-constantes-plan-referencia';
import { doc, Traza, unirFuentes } from './pe-tipos';
import { nivelPorPorcentaje, ResultadoVulnerabilidad } from './pe-vulnerabilidad';
import { FUENTE_TABLA19 } from './pe-zonas-sismicas';

const METODO = 'amenaza_vulnerabilidad';

export type ZonaSismica = 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI';

export const AMENAZA_POR_ZONA_SISMICA: Record<ZonaSismica, Nivel4> = {
  I: 'medio', II: 'alto', III: 'alto', IV: 'alto', V: 'alto', VI: 'muy_alto',
};

export function amenazaSismica(zona: ZonaSismica): Nivel4 {
  return AMENAZA_POR_ZONA_SISMICA[zona];
}

// Vulnerabilidad desde el % de cumplimiento normativo, con las mismas bandas del punto 17.
export function vulnerabilidadDesdeCumplimiento(porcentaje: number): Nivel4 {
  if (porcentaje < 0 || porcentaje > 100) throw new Error(`Porcentaje de cumplimiento inválido: ${porcentaje}`);
  return nivelPorPorcentaje(porcentaje);
}

// % de cumplimiento de la verificación normativa: cumple / (cumple + no cumple); las trazas
// informativas y por validar no cuentan. Sin comprobaciones evaluadas, null.
export function porcentajeCumplimiento(trazas: Traza[]): number | null {
  const evaluadas = trazas.filter(t => t.estado === 'cumple' || t.estado === 'no_cumple');
  if (evaluadas.length === 0) return null;
  return (100 * evaluadas.filter(t => t.estado === 'cumple').length) / evaluadas.length;
}

export type EntradaNivelAmenaza =
  | { tipo: 'sismo'; zonaSismica: ZonaSismica }
  | { tipo: 'volcan' | 'inundacion' | 'otra'; nivel: Nivel4; fuente: string };

export type EntradaVulnerabilidad =
  | { modo: 'manual'; nivel: Nivel4 }
  | { modo: 'cumplimiento'; porcentaje: number }
  | { modo: 'criterios'; resultado: ResultadoVulnerabilidad }; // vulnerabilidadPorAmenaza (punto 17)

export interface EntradaAmenaza {
  amenaza: string; // p. ej. "Sismos y terremotos"
  nivelAmenaza: EntradaNivelAmenaza;
  vulnerabilidad: EntradaVulnerabilidad;
}

export function evaluarAmenaza(e: EntradaAmenaza) {
  const trazas: Traza[] = [];
  let nivelAmenaza: Nivel4;
  if (e.nivelAmenaza.tipo === 'sismo') {
    nivelAmenaza = amenazaSismica(e.nivelAmenaza.zonaSismica);
    trazas.push({
      metodo: METODO, norma: 'NEC-SE-DS', articulo_o_tabla: '§10.2, Tabla 19 (factor Z por población)',
      umbral: null, valor: `zona ${e.nivelAmenaza.zonaSismica} → ${nivelAmenaza}`, unidad: '—', estado: 'informativo',
      fuente_doc: unirFuentes(FUENTE_TABLA19, doc.paso0('§7 PE-20')),
      nota: 'I → media; II a V → alta; VI → muy alta. Zona del capítulo 1: población de la Tabla 19 o ajuste manual del administrador',
    });
  } else {
    if (!e.nivelAmenaza.fuente || !e.nivelAmenaza.fuente.trim()) {
      throw new Error(`La amenaza "${e.amenaza}" requiere su fuente (mapa del IGEPN o PDOT del GAD)`);
    }
    nivelAmenaza = e.nivelAmenaza.nivel;
    trazas.push({
      metodo: METODO, norma: 'Fuente indicada por el responsable', articulo_o_tabla: `Amenaza (${e.nivelAmenaza.tipo})`,
      umbral: null, valor: nivelAmenaza, unidad: '—', estado: 'informativo', fuente_doc: doc.paso0('§7 PE-20'),
      nota: `Fuente: ${e.nivelAmenaza.fuente.trim()}`,
    });
  }

  let vulnerabilidad: Nivel4;
  if (e.vulnerabilidad.modo === 'criterios') {
    vulnerabilidad = e.vulnerabilidad.resultado.nivel;
    trazas.push(...e.vulnerabilidad.resultado.trazas);
  } else if (e.vulnerabilidad.modo === 'cumplimiento') {
    vulnerabilidad = vulnerabilidadDesdeCumplimiento(e.vulnerabilidad.porcentaje);
    trazas.push({
      metodo: METODO, norma: 'Criterio técnico de StorSafe (por validar)', articulo_o_tabla: 'Vulnerabilidad desde el cumplimiento',
      umbral: '≥ 90 baja; 70–89 media; 50–69 alta; < 50 muy alta', valor: e.vulnerabilidad.porcentaje, unidad: '%',
      estado: 'por_validar', fuente_doc: doc.paso0('§7 PE-19'),
      nota: `Vulnerabilidad ${etiquetaNivel(vulnerabilidad, 'f')} por el % de cumplimiento de la verificación normativa (por validar)`,
    });
  } else {
    vulnerabilidad = e.vulnerabilidad.nivel;
    trazas.push({
      metodo: METODO, norma: 'Criterio del responsable', articulo_o_tabla: 'Vulnerabilidad elegida a mano',
      umbral: null, valor: vulnerabilidad, unidad: '—', estado: 'por_validar', fuente_doc: doc.paso0('§7 PE-19'),
      nota: 'Vulnerabilidad elegida a mano; lo recomendado son los criterios de preparación por amenaza',
    });
  }

  const riesgo = MATRIZ_AMENAZA_VULNERABILIDAD[nivelAmenaza][vulnerabilidad];
  trazas.unshift({
    metodo: METODO, norma: FUENTE_MATRIZ_AMENAZAS.norma, articulo_o_tabla: 'Matriz 4×4',
    umbral: null, valor: riesgo, unidad: '—', estado: 'informativo', fuente_doc: FUENTE_MATRIZ_AMENAZAS.fuente_doc,
    nota: `${e.amenaza}: amenaza ${etiquetaNivel(nivelAmenaza, 'f')} × vulnerabilidad ${etiquetaNivel(vulnerabilidad, 'f')} → riesgo ${etiquetaNivel(riesgo, 'm')}`,
  });
  return { amenaza: e.amenaza, nivelAmenaza, vulnerabilidad, riesgo, trazas };
}
